import React, { useEffect, useRef, useState } from "react";
import { TreePine, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useOffline } from "@/context/OfflineContext";
import { hiddenClientFields } from "@/lib/client-privacy";

export default function ClientSharingTreeStatus({ clientId }) {
  const { user } = useAuth();
  const { isOnline } = useOffline();
  const scope = JSON.stringify([clientId, user?.id, user?.role,
    user?.page_permissions, hiddenClientFields(user).slice().sort(), isOnline]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const [result, setResult] = useState(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!clientId || !isOnline) return;
    let active = true;
    const controller = new AbortController();
    setResult(null);
    api.get(`/clients/${clientId}/sharing-tree-registration`, { signal: controller.signal })
      .then(({ data }) => {
        if (!Number.isInteger(data?.year) || typeof data?.registered !== "boolean") throw new Error("Invalid status");
        if (active && currentScope.current === scope) setResult({ scope, data });
      }).catch(() => {
        if (active && currentScope.current === scope) setResult({ scope, error: true });
      });
    return () => { active = false; controller.abort(); };
  }, [clientId, isOnline, scope, retry]);
  useEffect(() => {
    const refresh = () => setRetry(n => n + 1);
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, []);
  const state = result?.scope === scope ? result : null;
  const data = isOnline && clientId ? state?.data : null;
  const unavailable = !isOnline || state?.error;
  const text = data ? `Sharing Tree ${data.year}: ${data.registered ? "Registered" : "Not registered"}`
    : unavailable ? `Sharing Tree: Status unavailable${!isOnline ? " — offline" : ""}`
    : "Sharing Tree: Checking registration…";
  return (
    <div className="mt-3 flex items-center flex-wrap gap-2 text-sm" data-testid="client-sharing-tree-status">
      <span role="status" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-medium"
        style={{ background: data?.registered ? "var(--fb-success-bg)" : "var(--fb-bg)",
          color: data?.registered ? "var(--fb-success)" : "var(--fb-text-muted)" }}>
        <TreePine className="h-4 w-4 shrink-0" aria-hidden="true" />{text}
      </span>
      <button type="button" onClick={() => setRetry(n => n + 1)} disabled={!isOnline || !clientId}
        className="inline-flex items-center gap-1 text-xs underline disabled:opacity-50"
        aria-label="Refresh Sharing Tree registration status">
        <RefreshCw className="h-3 w-3" aria-hidden="true" />Refresh
      </button>
    </div>
  );
}
